// ============================================================
// permissionConfig.js — single source of truth for domains/functions.
//
// UPDATE THIS FILE whenever you ship a new domain or a new function
// inside an existing domain. Both App.js (route gating) and
// AdminConsole.jsx (permission toggle UI) read from this file — nothing
// else needs to change for a new entry here to show up as a toggle and
// be enforceable, EXCEPT: you still need to add the actual <Route> in
// App.js yourself and wire its `element` to check hasFunctionAccess()
// for the new function key. This file is the manifest, not the router.
// ============================================================

export const ROLES = ['admin', 'kam', 'accounts', 'scm', 'dispatch', 'warehouse', 'mis', 'sales'];

export const ROLE_STYLE = {
    admin:     { tint: '#EEE9F8', color: '#6E58A6' },
    kam:       { tint: '#E7F0E8', color: '#5C7E60' },
    accounts:  { tint: '#E3F2F0', color: '#2F8F82' },
    scm:       { tint: '#F5EDDC', color: '#9A7B3E' },
    dispatch:  { tint: '#E5EEF7', color: '#4A6FA5' },
    warehouse: { tint: '#F1EEF6', color: '#3A3A3A' },
    mis:       { tint: '#E8ECFB', color: '#4C5FA8' },
    sales:     { tint: '#F7E5EA', color: '#B05770' },
};

// Domains, in display order. `functions` is what actually gets toggled
// per-user in the Admin Console and checked via hasFunctionAccess().
export const DOMAINS = [
    {
        key: 'sales', label: 'Sales', color: '#6E58A6', tint: '#EEE9F8',
        functions: [
            { key: 'dashboard',    label: 'Sales Dashboard', path: '/sales' },
            { key: 'festive_season_trends', label: 'Festive Season Trends', path: '/sales/festive-season-trends' },
        ],
    },
    {
        key: 'inventory', label: 'Inventory', color: '#5C7E60', tint: '#E7F0E8',
        functions: [
            { key: 'stock_dashboard', label: 'Stock Dashboard', path: '/inventory' },
            { key: 'inward',          label: 'Inward Entry',    path: '/inventory/inward' },
            { key: 'outward',         label: 'Outward Entry',   path: '/inventory/outward' },
            { key: 'sku_master',      label: 'SKU Master',      path: '/inventory/sku-desc' },
            { key: 'card_inventory',  label: 'Card Inventory',  path: '/inventory/cards' },
        ],
    },
    {
        key: 'dms', label: 'DMS', color: '#9A7B3E', tint: '#F5EDDC',
        functions: [
            { key: 'uprising_inventory', label: 'Uprising Inventory', path: '/dms' },
            { key: 'fatehpuria_inventory', label: 'Fatehpuria Inventory', path: '/dms/fatehpuria-inventory' },
            { key: 'choudhary_inventory', label: 'Choudhary Inventory', path: '/dms/choudhary-inventory' },
            // `key` stays 'distributor_po' deliberately — moved here from
            // po_so_lifecycle (where it actually belongs is DMS), but renaming
            // the key would revoke the page from every user who has it until
            // the DB is migrated. Same precedent as grn_management's rename.
            { key: 'distributor_po', label: 'Distributor Purchase Orders', path: '/dms/distributor-po' },
            { key: 'distributor_master', label: 'Distributor Master', path: '/dms/distributor-master' },
        ],
    },
    {
        key: 'platform', label: 'Platform', color: '#B05770', tint: '#F7E5EA',
        functions: [
            { key: 'sku_availability', label: 'SKU Availability', path: '/platform/availability' },
        ],
    },
    {
        key: 'po_so_lifecycle', label: 'PO & SO Lifecycle', color: '#4A6FA5', tint: '#E5EEF7',
        functions: [
            { key: 'supply_chain_dashboard', label: 'Supply Chain Analytics Dashboard', path: '/po-so-lifecycle/supply-chain-dashboard' },
            { key: 'po_tracker',        label: 'PO Tracker',            path: '/po-so-lifecycle/po-tracker' },
            { key: 'po_tracker_cancel', label: 'PO Tracker — Cancel',   path: '/po-so-lifecycle/po-tracker' },
            { key: 'po_tracker_delete', label: 'PO Tracker — Delete',   path: '/po-so-lifecycle/po-tracker' },
            { key: 'b2b_dispatch',  label: 'B2B Dispatch',   path: '/po-so-lifecycle/b2b-dispatch' },
            { key: 'b2b_update_po_record', label: 'B2B Dispatch — Update PO Record', path: '/po-so-lifecycle/b2b-dispatch' },
            { key: 'so_kam_review', label: 'SO KAM Review',  path: '/po-so-lifecycle/so-kam-review' },
            // `key` stays 'grn_management' deliberately: it's persisted per-user
            // in portal_users.function_permissions, so renaming it would revoke
            // the page from every user who has it until the DB is migrated.
            // Only the display label was renamed to "Goods Received Note".
            { key: 'grn_management', label: 'Goods Received Note', path: '/po-so-lifecycle/grn-management' },
            { key: 'rtv_record',     label: 'RTV Record', path: '/po-so-lifecycle/rtv-record' },
            { key: 'box_images',     label: 'Packed Box Images', path: '/po-so-lifecycle/box-images' },
            { key: 'institutional_orders', label: 'Institutional Orders', path: '/po-so-lifecycle/institutional-orders' },
            { key: 'gifting_dispatch', label: 'Gifting & Dispatch', path: '/po-so-lifecycle/gifting-dispatch' },
            { key: 'po_register_data', label: 'PO Register Data', path: '/po-so-lifecycle/po-register-data' },
        ],
    },
    {
        key: 'invoicing', label: 'Invoicing', color: '#2F8F82', tint: '#E3F2F0',
        functions: [
            { key: 'invoice_queue', label: 'Invoice Queue', path: '/invoicing/invoice-queue' },
            { key: 'so_queue',      label: 'Sales Orders',  path: '/invoicing/sales-orders' },
            // Label/path made generic — this page now shows POs from both
            // Distributor and Institutional sources (see proforma-invoice.js).
            // `key` stays 'distributor_performa_invoice' deliberately, same
            // precedent as grn_management/distributor_po above: it's persisted
            // per-user in portal_users.function_permissions, so renaming it
            // would revoke the page from every user who has it until migrated.
            { key: 'distributor_performa_invoice', label: 'Proforma Invoice', path: '/invoicing/distributor-performa-invoice' },
            { key: 'invoices',      label: 'Invoices',      path: '/invoicing/invoices' },
            { key: 'so_vs_invoice', label: 'SO vs Invoice', path: '/invoicing/so-vs-invoice' },
            // No longer its own page — this key now grants the "Cancel SO"
            // action inside Sales Orders. Kept as a separate key (rather than
            // folded into so_queue) so the privilege stays independently
            // grantable, and so nobody's saved function_permissions needed a
            // migration. Path points at Sales Orders so firstAccessiblePath()
            // can never route someone to a dead URL.
            { key: 'cancel_so',     label: 'Cancel SO (action)', path: '/invoicing/sales-orders' },
        ],
    },
    {
        key: 'warehouse', label: 'Warehouse', color: '#3A3A3A', tint: '#F1EEF6',
        functions: [
            { key: 'warehouse_access', label: 'Warehouse Access', path: '/warehouse' },
            // Search & view tile on the Warehouse mobile home (read-only).
            { key: 'packed_box_images', label: 'Packed Box Images', path: '/warehouse' },
        ],
    },
    {
        key: 'pod_upload', label: 'POD Upload', color: '#3A3A3A', tint: '#F1EEF6',
        functions: [
            { key: 'pod_upload_access', label: 'Upload Proof of Delivery', path: '/pod-upload' },
        ],
    },
    {
        key: 'd2c', label: 'D2C', color: '#B0553F', tint: '#F5E4DC',
        functions: [
            { key: 'shopify_tracker', label: 'Shopify Tracker', path: '/d2c/shopify-tracker' },
        ],
    },
];

// Default starting permissions applied when a new user is created with
// a given role. Just a starting point — fully editable per-user
// afterward in the Admin Console.
export const DEFAULT_PERMS_BY_ROLE = {
    admin: {
        sales: ['dashboard', 'festive_season_trends'],
        inventory: ['stock_dashboard', 'inward', 'outward', 'sku_master', 'card_inventory'],
        dms: ['uprising_inventory', 'fatehpuria_inventory', 'choudhary_inventory', 'distributor_po', 'distributor_master'],
        platform: ['sku_availability'],
        po_so_lifecycle: ['supply_chain_dashboard', 'po_tracker', 'po_tracker_cancel', 'po_tracker_delete', 'b2b_dispatch', 'b2b_update_po_record', 'so_kam_review', 'grn_management', 'box_images', 'institutional_orders', 'gifting_dispatch', 'po_register_data'],
        invoicing: ['invoice_queue', 'so_queue', 'distributor_performa_invoice', 'invoices', 'so_vs_invoice', 'cancel_so'],
        warehouse: ['warehouse_access', 'packed_box_images'],
        d2c: ['shopify_tracker'],
    },
    kam: {
        po_so_lifecycle: ['so_kam_review'],
        dms: ['distributor_po', 'distributor_master'],
    },
    accounts: {
        invoicing: ['invoice_queue', 'so_queue', 'distributor_performa_invoice', 'invoices', 'so_vs_invoice', 'cancel_so'],
    },
    scm: {
        inventory: ['stock_dashboard', 'inward', 'outward', 'sku_master', 'card_inventory'],
    },
    dispatch: {
        po_so_lifecycle: ['b2b_dispatch', 'b2b_update_po_record'],
    },
    warehouse: {
        warehouse: ['warehouse_access', 'packed_box_images'],
    },
    mis: {},
    sales: {
        po_so_lifecycle: ['institutional_orders'],
    },
};

// ── Access check helpers ────────────────────────────────────────────────
// Admin role bypasses everything unconditionally, matching prior behavior.

export function hasFunctionAccess(user, domainKey, functionKey) {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const perms = user.function_permissions || {};
    const domainFns = perms[domainKey] || [];
    return domainFns.includes(functionKey);
}

export function hasDomainAccess(user, domainKey) {
    if (!user) return false;
    if (user.role === 'admin') return true;
    const perms = user.function_permissions || {};
    return (perms[domainKey] || []).length > 0;
}

// Second permission dimension, layered on top of hasFunctionAccess (view).
// user.edit_permissions is null for every user until an admin explicitly
// sets a per-function edit level for them — until then, granted (view)
// access behaves exactly as it always has (full edit), so rollout requires
// no data migration/backfill.
export function hasEditAccess(user, domainKey, functionKey) {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (!hasFunctionAccess(user, domainKey, functionKey)) return false; // edit implies view
    if (user.edit_permissions == null) return true; // never explicitly restricted -> legacy full-access behavior
    return (user.edit_permissions[domainKey] || []).includes(functionKey);
}

// "kam" -> "KAM", "admin" -> "Admin", "accounts" -> "Accounts", etc.
export function formatRole(role) {
    if (!role) return '';
    return role.toLowerCase() === 'kam' ? 'KAM' : role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
}

// Path of the first function (in DOMAINS order) the user has access to
// within a domain — used to deep-link a domain tile click straight to
// something the user can actually open, instead of a hardcoded default.
export function firstAccessiblePath(user, domainKey) {
    const domain = DOMAINS.find(d => d.key === domainKey);
    if (!domain) return null;
    const fn = domain.functions.find(f => hasFunctionAccess(user, domainKey, f.key));
    return fn ? fn.path : null;
}

export function countGranted(functionPermissions) {
    let granted = 0, total = 0;
    DOMAINS.forEach(d => {
        d.functions.forEach(f => {
            total++;
            if ((functionPermissions?.[d.key] || []).includes(f.key)) granted++;
        });
    });
    return { granted, total };
}